// === src/pages/dashboard/Reports.jsx ===
import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import Loader from "../../components/Loader";
import { toast } from "react-toastify";

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState("All");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchReports = async () => {
      const response = await new Promise((resolve) =>
        setTimeout(() =>
          resolve([
            { vehicle: "Truck A", date: "2025-05-10", mileage: 2500, fuelCost: 300, fuel: 25 },
            { vehicle: "Truck B", date: "2025-05-11", mileage: 2650, fuelCost: 320, fuel: 28 },
            { vehicle: "Truck C", date: "2025-05-12", mileage: 2400, fuelCost: 280, fuel: 22 },
            { vehicle: "Truck A", date: "2025-05-13", mileage: 2700, fuelCost: 310, fuel: 27 },
            { vehicle: "Truck B", date: "2025-05-14", mileage: 2600, fuelCost: 305, fuel: 26 },
          ]), 300)
      );
      setReports(response);
      setFilteredReports(response);
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "auto" });
    };
    fetchReports();
  }, []);

  const filterByDateAndVehicle = () => {
    setProcessing(true);
    setTimeout(() => {
      let result = [...reports];
      if (fromDate && toDate) {
        result = result.filter(report => report.date >= fromDate && report.date <= toDate);
      }
      if (selectedVehicle !== "All") {
        result = result.filter(report => report.vehicle === selectedVehicle);
      }
      setFilteredReports(result);
      setProcessing(false);
    }, 400);
  };

  const exportPDF = () => {
    setProcessing(true);
    toast.info("Exporting PDF...");
    setTimeout(() => {
      const doc = new jsPDF();
      autoTable(doc, {
        head: [["Date", "Vehicle", "Mileage", "Fuel Cost ($)", "Fuel (gal)"]],
        body: filteredReports.map(({ date, vehicle, mileage, fuelCost, fuel }) => [date, vehicle, mileage, fuelCost, fuel]),
      });
      doc.save("reports-summary.pdf");
      setProcessing(false);
      toast.success("PDF exported");
    }, 300);
  };

  const exportExcel = () => {
    setProcessing(true);
    toast.info("Exporting Excel...");
    setTimeout(() => {
      const worksheet = XLSX.utils.json_to_sheet(filteredReports);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Reports");
      XLSX.writeFile(workbook, "reports-summary.xlsx");
      setProcessing(false);
      toast.success("Excel exported");
    }, 300);
  };

  const avgFuelEfficiency = (
    filteredReports.reduce((sum, r) => sum + r.mileage, 0) /
    (filteredReports.reduce((sum, r) => sum + r.fuel, 0) || 1)
  ).toFixed(2);

  const avgCostPerMile = (
    filteredReports.reduce((sum, r) => sum + r.fuelCost, 0) /
    (filteredReports.reduce((sum, r) => sum + r.mileage, 0) || 1)
  ).toFixed(2);

  const vehicles = ["All", ...new Set(reports.map(r => r.vehicle))];

  return (
    <div className="relative min-h-[600px]">
      {(loading || processing) && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-black/60 rounded">
          <Loader />
        </div>
      )}

      <div className={`${loading || processing ? "opacity-30 pointer-events-none" : ""}`}>
        <h1 className="text-2xl font-bold mb-4">Reports</h1>

        <div className="flex flex-wrap gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border px-2 py-1 rounded bg-white dark:bg-gray-800 text-black dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border px-2 py-1 rounded bg-white dark:bg-gray-800 text-black dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Vehicle</label>
            <select
              value={selectedVehicle}
              onChange={(e) => setSelectedVehicle(e.target.value)}
              className="border px-2 py-1 rounded bg-white dark:bg-gray-800 text-black dark:text-white"
            >
              {vehicles.map((v, i) => <option key={i}>{v}</option>)}
            </select>
          </div>
          <button onClick={filterByDateAndVehicle} className="bg-blue-500 text-white px-4 py-2 rounded self-end">Filter</button>
          <button onClick={exportPDF} className="bg-orange-500 text-white px-4 py-2 rounded self-end">Export PDF</button>
          <button onClick={exportExcel} className="bg-green-500 text-white px-4 py-2 rounded self-end">Export Excel</button>
        </div>

        <div className="mb-6">
          <p className="text-sm">
            Average Fuel Efficiency:{" "}
            <span className="font-bold">{avgFuelEfficiency} mi/gal</span>
          </p>
          <p className="text-sm">
            Average Cost per Mile:{" "}
            <span className="font-bold">${avgCostPerMile}</span>
          </p>
        </div>

        <div className="bg-gray-100 dark:bg-[#1e293b] p-6 rounded shadow min-h-[340px]">
          <h3 className="text-lg font-semibold mb-4">Mileage Summary</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={filteredReports}>
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="mileage" stroke="#4f46e5" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Reports;
